import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ScrollToTop } from "../../../components/scrollToTop";
import {
  useGetEventQuery,
  useGetEventTicketsQuery,
  useReleaseTicketsMutation,
} from "../../../redux/features/event/event.api";
import {
  CreatedTicket,
  EventGuest,
} from "../../../redux/features/event/event.types";
import { useLazyGetStripeSessionQuery } from "../../../redux/features/payment/payment.api";
import { RedirectSource, TicketPurchase } from "../../../utils/types";
import { PreviewLoader } from "./components/loader";
import { EventSoundtrackControl } from "./components/eventSoundtrack";
import { PreviewStep } from "./previewStep";
import { PurchaseTickets } from "./purchaseStep";
import { PurchaseSuccess } from "./purchaseSuccessStep";

const replaceStripeCallbackUrl = (slug?: string) => {
  if (!slug) return;

  window.history.replaceState(
    window.history.state,
    "",
    `/${encodeURIComponent(slug)}`,
  );
};

export const PreviewSingleEvent = ({
  canBuy = false,
  doHoldTicket = false,
}: {
  canBuy: boolean;
  doHoldTicket?: boolean;
}) => {
  const { slug, source, tikketPurchaseSessionId } = useParams<{
    slug: string;
    source?: RedirectSource;
    tikketPurchaseSessionId?: string;
  }>();

  const [searchParams, setSearchParams] = useSearchParams();

  const navigate = useNavigate();

  const [ticketsPurchase, setTicketsPurchase] = useState<TicketPurchase[]>([]);
  const [individualTicketsPurchase, setIndividualTicketsPurchase] = useState<
    CreatedTicket[]
  >([]);
  const [step, setStep] = useState<1 | 2 | "success">(1);
  const [eventTickets, setEventTickets] = useState<CreatedTicket[]>([]);
  const [guestDetails, setGuestDetails] = useState<EventGuest>();
  const [isPurchasingOnlyFree, setIsPurchasingOnlyFree] =
    useState<boolean>(false);

  const {
    data: eventResponse,
    isLoading: fetchingEvent,
    isError: eventFetchError,
  } = useGetEventQuery(
    {
      slug: slug,
    },
    { refetchOnMountOrArgChange: true },
  );

  const {
    data: tickets,
    isLoading: fetchingTickets,
    refetch: refetchTickets,
  } = useGetEventTicketsQuery(
    {
      id: eventResponse?.data._id ?? "",
    },
    {
      refetchOnMountOrArgChange: true,
      skip: fetchingEvent || !eventResponse?.data?._id,
      pollingInterval: 10000,
    },
  );

  const [getStripeSession, { isFetching: fetchingStripeSession }] =
    useLazyGetStripeSessionQuery();

  const [releaseTickets, { isLoading: releasingTickets }] =
    useReleaseTicketsMutation();

  useEffect(() => {
    const ticketPurchaseBucket = ticketsPurchase.map((tp) => {
      return Array.from({ length: tp.quantity }, (_, i) => tp.ticket);
    });

    setIndividualTicketsPurchase(ticketPurchaseBucket.flat(1));

    // Check whether only free tickets are being purchased
    setIsPurchasingOnlyFree(
      ticketsPurchase
        .map((tp) => tp.ticket)
        .every((t) => t.price === 0 || t.ticketType === "free"),
    );
  }, [ticketsPurchase]);

  // Check event is still active for purchase or throw 404
  useEffect(() => {
    if (fetchingEvent) {
      return;
    }

    if (
      (canBuy && eventResponse?.data?.status === "draft") ||
      ["completed", "cancelled", undefined].includes(
        eventResponse?.data.status,
      ) ||
      !!eventResponse?.data?.isFlagged ||
      eventFetchError
    ) {
      navigate("/404");
    }
    // eslint-disable-next-line
  }, [fetchingEvent, eventResponse, eventFetchError]);

  // Handle tickets release and resumption from redirects
  useEffect(() => {
    const stripeCanceled = searchParams.get("canceled") === "true";

    // Release session tickets
    if (
      step === 1 &&
      !releasingTickets &&
      (!!searchParams.get("session") || !!tikketPurchaseSessionId) &&
      (!source || (source === "stripe" && stripeCanceled)) //Release tickets when stripe paymet is canceled
    ) {
      releaseTickets({
        sessionId: !!source
          ? (tikketPurchaseSessionId ?? "")
          : (searchParams.get("session") ?? ""),
      }).then(() => {
        setSearchParams({});
        refetchTickets();
      });
    }

    // Handle stripe redirects
    const stripeSessionId = searchParams.get("session_id");
    if (step !== "success" && source === "stripe" && !!stripeSessionId) {
      getStripeSession({
        session_id: stripeSessionId,
      })
        .then((resp) => {
          if (resp.data?.status === "success") {
            setGuestDetails(resp.data.data);
            setStep("success");
            setTicketsPurchase(
              resp.data.data.tickets.map((t) => ({
                quantity: t.quantity,
                ticket: t.ticketId,
              })),
            );
            replaceStripeCallbackUrl(slug);
          } else {
            navigate("/");
          }
        })
        .catch(() => {
          navigate("/");
        });
    }

    // eslint-disable-next-line
  }, [step, searchParams]);

  // Set tickets
  useEffect(() => {
    if (tickets?.data?.tickets) {
      setEventTickets(tickets.data.tickets);
    }
  }, [tickets]);

  return fetchingEvent || fetchingTickets || fetchingStripeSession ? (
    <PreviewLoader />
  ) : (
    <>
      <ScrollToTop changeKey={step} rerunOnKeyChange scrollIntoView />
      {eventResponse?.data !== undefined && step !== "success" ? (
        <EventSoundtrackControl soundtrack={eventResponse.data.soundtrack} />
      ) : undefined}

      {eventResponse?.data !== undefined && step === 1 ? (
        <>
          <PreviewStep
            event={eventResponse?.data!}
            tickets={eventTickets}
            ticketsPurchase={ticketsPurchase}
            setTicketsPurchase={setTicketsPurchase}
            setStep={setStep}
            canBuy={canBuy}
            showBack
            doHoldTicket={doHoldTicket}
            refetchTickets={() => {
              refetchTickets();
            }}
          />
        </>
      ) : undefined}

      {eventResponse?.data !== undefined && step === 2 ? (
        <PurchaseTickets
          event={eventResponse?.data!}
          tickets={eventTickets}
          ticketsPurchase={ticketsPurchase}
          setTicketsPurchase={setTicketsPurchase}
          setStep={setStep}
          setGuestDetails={setGuestDetails}
          individualTicketsPurchase={individualTicketsPurchase}
          doHoldTicket={doHoldTicket && !!searchParams.get("session")}
          sessionId={searchParams.get("session") ?? undefined}
          refetchTickets={() => {
            refetchTickets();
          }}
          isPurchasingOnlyFree={isPurchasingOnlyFree}
        />
      ) : undefined}

      {!!guestDetails && step === "success" ? (
        <PurchaseSuccess
          guest={guestDetails!}
          ticketsPurchase={ticketsPurchase}
          isPurchasingOnlyFree={isPurchasingOnlyFree}
        />
      ) : undefined}
    </>
  );
};
